import { gql, useLazyQuery } from "@apollo/client";
import DOMPurify from "dompurify";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useHistory } from "react-router-dom";
import {
  calendar,
  download,
  google,
  mail_incoming,
  mail_outgoing,
  phone_incoming,
  phone_outgoing,
  user,
  users,
} from "src/images/NewDesign";
import { OptionItem } from "src/types";
import { formatPhoneNumber, getCommunicationItemRepPhoneNumber } from "src/utils/format";
import { displaySanitizedHTML, downloadResourceCors } from "src/utils/misc";
import { theme } from "src/utils/theme";
import styled from "styled-components";
import { SearchMenuV2, SequenceTag } from "../Pages/DashboardV2";
import { formatTime } from "../Segments/CallReportSegments/WaveformV2";
import { AppText, FlexDiv, Loading } from "../UI";
import { PhoenixAppButton, PhoenixIcon, PhoenixMultiSelect } from "../UI/Phoenix";

const FETCH_RECENT_ACTIVITY = gql`
  query fetchRecentActivities($skip: Int, $take: Int, $communication_type: CommunicationType) {
    fetchRecentActivities(skip: $skip, take: $take, communication_type: $communication_type) {
      id
      title

      channel
      communication_type

      created_at

      conference {
        id
        recording_url_computed
      }

      lead {
        id
        business_name
        sequence_step_id
        full_name
        city
        associate_parent_id
      }

      customer_number
      local_number {
        id
        number
      }
      rep_phone_number

      email_item {
        id
        from_email
        to_email
        body
        html
      }

      SMSItem {
        id
        lead_number_used
        text
      }

      related_disposition {
        id
        label
        computed_call_history_theme {
          completed_with_theme {
            label
            bg_color
            font_color
          }
          call_result_theme {
            label
            bg_color
            font_color
          }
        }
      }
    }
  }
`;

const TAKE = 8;

interface IActivity {
  id: string;
  title: string | null;
  communication_type: string;
  created_at: string;
  channel: string;

  customer_number: string | null;
  rep_phone_number: string;

  conference: {
    id: string;
    recording_url_computed: string | null;
    duration: number | null;
  } | null;

  lead: {
    id: string;
    business_name: string;
    sequence_step_id: string | null;
    full_name?: string;
    city?: string;
    associate_parent_id: string | null;
  };

  email_item: {
    id: string;
    from_email: string;
    to_email: string;
    body: string | null;
    html: string | null;
  } | null;
  SMSItem: {
    id: string;
    lead_number_used: string;
    text: string | null;
  } | null;

  related_disposition: {
    id: string;
    label: string;
    computed_call_history_theme: {
      completed_with_theme: {
        label: string;
        bg_color: string;
        font_color: string;
      };
      call_result_theme: {
        label: string;
        bg_color: string;
        font_color: string;
      };
    };
  } | null;
}

export const RecentActivityList: React.FC = () => {
  const [activities, setActivities] = useState<any[]>([]);
  const [skip, setSkip] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(true);

  const [typeFilter, setTypeFilter] = useState<OptionItem | undefined>(undefined);

  const [fetchRecentActivities, { data: dataRecents, loading: loadingRecents }] = useLazyQuery(FETCH_RECENT_ACTIVITY, {
    fetchPolicy: "network-only",
    onCompleted() {
      const newList = [...activities, ...dataRecents.fetchRecentActivities];
      if (dataRecents.fetchRecentActivities.length < TAKE) {
        setHasMore(false);
      }
      setActivities(newList);
    },
    onError({ message, name }) {
      console.log(`Error in ${name}: `, message);
    },
  });

  useEffect(() => {
    fetchRecentActivities({
      variables: {
        take: TAKE,
        skip: 0,
        communication_type: typeFilter?.value === "All" ? undefined : typeFilter?.value,
      },
    });
  }, [typeFilter]);

  const handleShowMore = useCallback(() => {
    fetchRecentActivities({
      variables: {
        take: TAKE,
        skip: skip + TAKE,
        communication_type: typeFilter?.value === "All" ? undefined : typeFilter?.value,
      },
    });
    setSkip((prevSkip) => prevSkip + TAKE);
  }, [TAKE, skip, setSkip, typeFilter]);

  const handleTypeChange = useCallback(
    (option: OptionItem) => {
      setTypeFilter(option);
      setActivities([]);
      setSkip(0);
      setHasMore(true);
    },
    [setTypeFilter, setSkip, setActivities, setHasMore],
  );

  return (
    <>
      <FlexDiv justify="space-between" align="center" padding="0px 0px 8px 16px">
        <PhoenixMultiSelect
          isMulti={false}
          name="type"
          options={[
            { label: "All", value: "All" },
            { label: "Phone", value: "Phone" },
            { label: "SMS", value: "SMS" },
            { label: "Email", value: "Email" },
          ]}
          placeholder="Type"
          onChange={handleTypeChange}
          value={typeFilter}
          isClearable={false}
          marginBottom={false}
          error={false}
          ghostSelect
          ghostSelectAppThemeOverride="Light"
          width={64}
          height={24}
          minHeight={24}
          optionsContainerWidth={120}
          style={{ overflow: "hidden" }}
          lineHeight="10px"
          fontSize="12px"
          pointer
          menuBorder
          removeMenuPadding
          menuPortal
        />
      </FlexDiv>
      <ListContainer id="activity-scroll-div">
        <InfiniteScroll
          dataLength={activities.length}
          next={handleShowMore}
          hasMore={hasMore}
          loader={
            <div style={{ padding: "8px 0px" }}>
              <Loading />
            </div>
          }
          scrollableTarget="activity-scroll-div"
        >
          {activities.map((activity: IActivity) => (
            <Activity key={activity.id} data={activity} />
          ))}
        </InfiniteScroll>

        {!activities.length && !loadingRecents && (
          <FlexDiv
            width="100%"
            height="100%"
            justify="center"
            align="center"
            direction="column"
            gap={24}
            padding="0px 0px 86px 0px"
          >
            <PhoenixIcon
              svg={calendar}
              size={40}
              color={theme.icon.neutral.default}
              hoverColor={theme.icon.neutral.default}
            />
            <AppText fontSize={14} fontWeight={400} lineHeight={20} color={theme.text.neutral.tertiary}>
              No history
            </AppText>
          </FlexDiv>
        )}
      </ListContainer>
    </>
  );
};

const ListContainer = styled.div`
  max-height: calc(100vh - 152px);
  height: 100%;
  padding: 0px 16px 16px 16px;
  overflow-y: auto;
`;

interface IActivityProps {
  data: IActivity;
}

const Activity: React.FC<IActivityProps> = ({ data }) => {
  const history = useHistory();

  const [showSearchMenu, setShowSearchMenu] = useState(false);

  const [loadingDownload, setLoadingDownload] = useState(false);

  const title = useMemo(() => {
    const incoming = data.channel === "Inbound";
    switch (data.communication_type) {
      case "Phone":
        if (data.title) return data.title;
        return incoming ? "Incoming Call" : "Outgoing Call";
      case "SMS":
        return incoming ? "SMS Received" : "SMS Sent";
      case "Email":
        return incoming ? "Email Received" : "Email Sent";
      case "ExternalTransfer":
        return "External Transfer";
      default:
        return data.title;
    }
  }, [data]);

  const dispositionData = useMemo(() => data?.related_disposition?.computed_call_history_theme, [data]);

  const contactRowData = useMemo(() => {
    const isEmail = data.communication_type === "Email";
    const isSMS = data.communication_type === "SMS";

    return {
      recipientIcon: isEmail ? mail_incoming : phone_incoming,
      originIcon: isEmail ? mail_outgoing : phone_outgoing,
      recipientContact: getRecipientContact(data, isEmail, isSMS),
      originContact: getOriginContact(data, isEmail, isSMS),
    };
  }, [data]);

  return (
    <ActivityContainer>
      <ActivityHeader>
        <AppText
          fontSize={14}
          fontWeight={600}
          lineHeight={20}
          color={theme.text.brand.primary}
          noWrap
          style={{ maxWidth: "240px", textOverflow: "ellipsis", overflow: "hidden" }}
        >
          {title}
        </AppText>

        <FlexDiv align="center" gap={4}>
          <AppText fontSize={12} fontWeight={400} lineHeight={18}>
            {moment(data.created_at).format("MM/DD/YYYY")}
          </AppText>
          <AppText fontSize={12} fontWeight={400} lineHeight={18} color={theme.text.neutral.secondary}>
            {moment(data.created_at).format("hh:mm A")}
          </AppText>
        </FlexDiv>
      </ActivityHeader>

      <ActivityBody>
        {showSearchMenu && (
          <SearchMenuV2
            setIsSearchClicked={setShowSearchMenu}
            businessName={data?.lead?.business_name}
            fullName={data?.lead?.full_name}
            city={data?.lead?.city}
            top="8px"
            right="68px"
          />
        )}

        <Row>
          <FlexDiv justify="space-between" align="center" width="100%" padding="0px 8px">
            <LeadIdentifierText
              fontSize={16}
              fontWeight={500}
              lineHeight={22}
              noWrap
              key={data?.lead?.business_name}
              onClick={() => history.push(`/lead-detail/${data?.lead?.id}`)}
            >
              {data?.lead?.business_name || "N/A"}
            </LeadIdentifierText>
            <FlexDiv gap={8} align="center">
              <img
                src={google}
                style={{ width: "22px", height: "22px", cursor: "pointer" }}
                loading="lazy"
                alt={`google ${data?.lead?.business_name || "N/A"}`}
                onClick={() => setShowSearchMenu(true)}
              />
              {!!data?.lead?.sequence_step_id && <SequenceTag />}
            </FlexDiv>
          </FlexDiv>
        </Row>
        <Row>
          <FlexDiv align="center" padding="0px 8px">
            <AppText
              fontSize={12}
              fontWeight={400}
              lineHeight={18}
              color={theme.text.neutral.tertiary}
              style={{ marginRight: "42px" }}
            >
              Contact
            </AppText>
            <FlexDiv align="center" gap={8}>
              <UserTag associated={!!data?.lead?.associate_parent_id}>
                <PhoenixIcon
                  svg={!data?.lead?.associate_parent_id ? user : users}
                  size={12}
                  color={theme.icon.neutral.inverse}
                  hoverColor={theme.icon.neutral.inverse}
                />
              </UserTag>
              <AppText fontSize={12} fontWeight={400} lineHeight={18}>
                {data?.lead?.full_name || "N/A"}
              </AppText>
            </FlexDiv>
          </FlexDiv>
        </Row>

        {(data.communication_type === "Phone" || data.communication_type === "ExternalTransfer") &&
          !!data?.related_disposition && (
            <Row>
              <FlexDiv align="center" padding="0px 8px">
                <AppText
                  fontSize={12}
                  fontWeight={400}
                  lineHeight={18}
                  color={theme.text.neutral.tertiary}
                  style={{ marginRight: "25px" }}
                >
                  Disposition
                </AppText>
                <FlexDiv align="center" gap={8}>
                  <DispositionTag
                    fontColor={dispositionData?.completed_with_theme?.font_color || theme.text.neutral.inverse}
                    bgColor={dispositionData?.completed_with_theme?.bg_color || theme.fill.brand.primary}
                  >
                    {dispositionData?.completed_with_theme?.label}
                  </DispositionTag>
                  <DispositionTag
                    fontColor={dispositionData?.call_result_theme?.font_color || theme.text.neutral.inverse}
                    bgColor={dispositionData?.call_result_theme?.bg_color || theme.fill.brand.primary}
                  >
                    {dispositionData?.call_result_theme?.label}
                  </DispositionTag>
                </FlexDiv>
              </FlexDiv>
            </Row>
          )}

        {data.communication_type !== "Internal" && (
          <Row>
            <FlexDiv align="center" gap={8} padding="0px 8px">
              <FlexDiv align="center" gap={4}>
                <PhoenixIcon
                  svg={contactRowData.recipientIcon}
                  size={16}
                  color={theme.icon.brand.default}
                  hoverColor={theme.icon.brand.default}
                />
                <AppText
                  fontSize={12}
                  fontWeight={400}
                  lineHeight={18}
                  style={{ maxWidth: "90%", wordWrap: "break-word" }}
                >
                  {contactRowData.recipientContact}
                </AppText>
              </FlexDiv>
              <FlexDiv align="center" gap={4}>
                <PhoenixIcon
                  svg={contactRowData.originIcon}
                  size={16}
                  color={theme.icon.brand.default}
                  hoverColor={theme.icon.brand.default}
                />
                <AppText
                  fontSize={12}
                  fontWeight={400}
                  lineHeight={18}
                  style={{ maxWidth: "90%", wordWrap: "break-word" }}
                >
                  {contactRowData.originContact}
                </AppText>
              </FlexDiv>
            </FlexDiv>
          </Row>
        )}

        {data?.conference?.id && data?.conference?.recording_url_computed && (
          <Row>
            <FlexDiv padding="0px 8px 0px 4px" justify="space-between" align="center" width="100%">
              <PhoenixAppButton
                variant="brand-outline"
                buttonType="ghost-small"
                onClick={() => history.push(`/call-report/${data?.conference?.id}`)}
              >
                Call Report
              </PhoenixAppButton>

              <FlexDiv align="center" gap={8}>
                <AppText fontSize={12} fontWeight={500} lineHeight={18}>
                  {formatTime(data?.conference?.duration ?? 0, (data?.conference?.duration ?? 0) > 3600)}
                </AppText>

                <PhoenixIcon
                  svg={download}
                  size={16}
                  pointer
                  color={loadingDownload ? theme.icon.brand.disabled : theme.icon.brand.default}
                  hoverColor={loadingDownload ? theme.icon.brand.disabled : theme.icon.brand.default}
                  onClick={() => {
                    if (loadingDownload) return;
                    setLoadingDownload(true);
                    downloadResourceCors(data?.conference?.recording_url_computed, undefined, () =>
                      setLoadingDownload(false),
                    );
                  }}
                />
              </FlexDiv>
            </FlexDiv>
          </Row>
        )}

        {data?.SMSItem && data?.SMSItem?.text && (
          <Row>
            <FlexDiv direction="column" padding="4px 8px 4px 4px">
              <PhoenixAppButton
                variant="brand-outline"
                buttonType="ghost-small"
                onClick={() => history.push(`/lead-detail/${data?.lead?.id}`)}
              >
                View Message
              </PhoenixAppButton>

              <AppText
                fontSize={12}
                fontWeight={400}
                lineHeight={18}
                color={theme.text.neutral.tertiary}
                style={{ paddingLeft: "4px" }}
              >
                {data?.SMSItem?.text}
              </AppText>
            </FlexDiv>
          </Row>
        )}

        {data?.email_item && (data?.email_item?.html || data?.email_item?.body) && (
          <Row>
            <FlexDiv direction="column" padding="4px 8px 4px 4px">
              <PhoenixAppButton
                variant="brand-outline"
                buttonType="ghost-small"
                onClick={() => history.push(`/lead-detail/${data?.lead?.id}`)}
              >
                View Message
              </PhoenixAppButton>

              <AppText
                fontSize={12}
                fontWeight={400}
                lineHeight={18}
                color={theme.text.neutral.tertiary}
                style={{ paddingLeft: "4px" }}
              >
                {DOMPurify.sanitize(
                  displaySanitizedHTML(data?.email_item?.html ?? (data?.email_item?.body || "")) || "",
                )}
              </AppText>
            </FlexDiv>
          </Row>
        )}
      </ActivityBody>
    </ActivityContainer>
  );
};

const ActivityContainer = styled.div`
  width: 100%;

  margin-bottom: 16px;

  border: 1px solid ${theme.border.neutral.primary};
  border-radius: 8px;

  overflow: hidden;

  animation: ${theme.popup} 0.2s ease-in-out forwards;
`;

const ActivityHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  width: 100%;
  min-height: 36px;
  max-height: 36px;

  padding: 8px 16px;

  background-color: ${theme.fill.brand.secondary};
`;

const ActivityBody = styled.div`
  position: relative;
  padding: 0px 8px;
  background-color: ${theme.fill.neutral.primary};

  & :last-child {
    border-bottom: none;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;

  width: 100%;
  min-height: 40px;

  border-bottom: 1px solid ${theme.border.neutral.secondary};
`;

const LeadIdentifierText = styled(AppText)`
  border-bottom: none;

  :hover {
    cursor: pointer;
    color: ${theme.text.brand.primary};
  }
  transition: color 0.2s ease-in-out;

  animation: ${theme.fadeIn} 1s ease forwards;

  max-width: 316px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserTag = styled.div<{ associated: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;

  min-width: 20px;
  max-width: 20px;
  min-height: 20px;
  max-height: 20px;

  border-radius: 2px;
  background-color: ${({ associated }) => (associated ? theme.fill.brand.inverse : theme.fill.brand.primary)};
`;

const DispositionTag = styled.div<{ fontColor: string; bgColor: string }>`
  display: flex;
  justify-content: center;
  align-items: center;

  width: fit-content;
  height: 24px;
  padding: 4px 8px;

  border-radius: 4px;
  background-color: ${({ bgColor }) => bgColor};

  color: ${({ fontColor }) => fontColor};
  font-size: 8px;
  font-weight: 600;
  line-height: 12px;
  text-transform: uppercase;
`;

const getRecipientContact = (data: IActivity, isEmail: boolean, isSMS: boolean) => {
  if (isEmail) {
    return data?.email_item?.to_email ?? "NA";
  }

  if (isSMS) {
    return data.channel === "Outbound"
      ? formatPhoneNumber(data?.SMSItem?.lead_number_used)
      : getCommunicationItemRepPhoneNumber(data as any);
  }

  return data.channel === "Outbound"
    ? formatPhoneNumber(data.customer_number)
    : getCommunicationItemRepPhoneNumber(data as any);
};
const getOriginContact = (data: IActivity, isEmail: boolean, isSMS: boolean) => {
  if (isEmail) {
    return data?.email_item?.from_email ?? "NA";
  }

  if (isSMS) {
    return data.channel === "Outbound"
      ? getCommunicationItemRepPhoneNumber(data as any)
      : formatPhoneNumber(data?.SMSItem?.lead_number_used);
  }

  return data.channel === "Outbound"
    ? getCommunicationItemRepPhoneNumber(data as any)
    : formatPhoneNumber(data.customer_number);
};
